import { useMemo, useState } from 'react';
import moment from 'moment';
import { getAlertsSummaryData } from '@src/api/aiAlerts';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { aiAlertsDimensionMappingSelector } from '@store/common/selectors';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { get, isEmpty } from 'lodash';
import { filterAlerts, sortColumn } from './utils';
import { trackQueryError } from '@utils/sensor/apptracker';
import { formatAIAlertSummary } from '@src/components/ai-alerts/formatter';
import { CURRENT_PRODUCT_MODE, isAppModule } from '@src/utils/constants';
import { SortOrder } from 'react-base-table';
import { useFetchAlertsForRootCause } from '@src/components/ai-alerts/hooks/useFetchAlertsForRootCauseWithPagination';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';

export function useFetchCauseAlerts({ interval, enabled = false, selectedAlert, sortBy }) {
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  const defaultValue = [];

  const { alertId = null } = selectedAlert;

  // stale time prevents redundant calls from multiple subscribers
  const { data: response, isFetching } = useQuery(
    ['ai-alert-summary-widgets', alertId, interval],
    () => {
      return getAlertsSummaryData({ params: { ...interval, cache: true } }).then((response) => {
        const data = get(response, ['data'], {});
        if (!isEmpty(data)) {
          const filteredAlertData = filterAlerts(selectedAlert, data);
          const alerts = formatAIAlertSummary(
            filteredAlertData,
            dimensionListObject as any,
            dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()],
          );
          return alerts;
        }
        return [];
      });
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: enabled && !!interval && !!alertId,
      staleTime: 5 * 60 * 1000, // 5m
      placeholderData: defaultValue,
      onError: (err) => {
        trackQueryError({ type: 'ai-alert-summary', err });
      },
    },
  );

  const data = useMemo(() => {
    if (!enabled || isEmpty(response)) {
      return defaultValue;
    }
    return response;
  }, [enabled, response]);

  const alerts = data;

  if (sortBy) {
    sortColumn(sortBy, alerts);
  }

  return { alerts, isFetching };
}

function useCauseAlerts({ alert, enabled = true }) {
  const [sortBy, setSortBy] = useState({
    key: 'eventTime',
    order: 'desc' as SortOrder,
  });
  const onColumnSort = ({ key, order }: any) => {
    setSortBy({ key, order });
  };

  const interval = useMemo(() => {
    const to = moment().startOf('hour');
    const from = moment(to).subtract(30, 'days').startOf('hour');
    return { from, to };
  }, []);

  const selectedAlert = useMemo(() => {
    return get(alert, ['diagnosticsData', 'summary'], null);
  }, [alert]);

  // - EI: Uses useFetchCauseAlerts (legacy implementation)
  // - APP: Uses useFetchAlertsForRootCause (new implementation with pagination and server-side sorting)

  const enableDPIAlertPagination = useGlobalFeature(globalFeatureNames.DPI_ALERT_PAGINATION);
  const isPaginationEnabled = enableDPIAlertPagination && isAppModule;

  const { from, to } = interval;

  const resultWithoutPagination = useFetchCauseAlerts({
    interval: { from: from.format(), to: to.format() },
    enabled: enabled && !isPaginationEnabled, // Control execution via enabled
    selectedAlert,
    sortBy,
  });

  const resultWithPagination = useFetchAlertsForRootCause(
    interval,
    {
      limit: 20,
      rootCause: selectedAlert?.rootCause,
      dimensionName: selectedAlert?.dimensionName,
    },
    sortBy,
    true, // fetchAll results for the last 30 days for selected rootCause and value
    enabled && isPaginationEnabled, // Only enable for APP mode
  );

  // Select the appropriate result based on product mode
  const { alerts, isFetching } = isPaginationEnabled
    ? resultWithPagination
    : resultWithoutPagination;
  return { alerts, isFetching, interval, sortBy, onColumnSort };
}

export default useCauseAlerts;
